/**
 * 管理后台「模型与供应商」文案（韩、中、英）：供应商配置、渠道模型库、已发布模型、AI 自动选模、通用能力。
 *
 * 背景：页面按 Alice 的版式复刻，文案沿用 Alice 的措辞，品牌与数据来源按本仓适配（没有自营渠道、没有 Jev 熔断、没有网盘）。
 * 设计意图：按页面分组；后端新增的错误码在这里映射（common.ts 的错误表归共用文件，不在本页改），没映射到的回落到共用表。
 * 关键约束：韩文用 해요체；中文不用破折号和英文引号；页面里已有的 A.models、A.cm、A.status 文案直接复用，不在这里重复。
 */
import { errorText as baseErrorText } from './common.ts'
import { translate, type Entry, type Locale } from './core.tsx'

const e = (zh: string, ko: string, en: string): Entry => ({ zh, ko, en })

const CODES: Record<string, Entry> = {
  not_found: e('找不到這條記錄', '항목을 찾을 수 없어요', 'Not found'),
  already_exists: e('已經存在同名記錄', '같은 이름이 이미 있어요', 'Already exists'),
  in_use: e('仍被路由或模型引用，不能刪除', '라우트나 모델이 아직 사용 중이라 삭제할 수 없어요', 'Still referenced by routes or models'),
  policy_required: e('還沒有保存過自動選模策略，請先發布一份策略', '저장된 자동 선택 정책이 없어요. 먼저 정책을 게시해 주세요', 'No auto-routing policy saved yet; publish one first'),
  channel_model_disabled: e('這個上遊模型在渠道模型庫裡已停用', '채널 모델 라이브러리에서 비활성화된 업스트림 모델이에요', 'This upstream model is disabled in the channel model library'),
  empty_patch: e('沒有任何要修改的欄位', '변경할 항목이 없어요', 'Nothing to change'),
  invalid_channel: e('渠道不存在', '존재하지 않는 채널이에요', 'Channel not found'),
  invalid_channel_model: e('渠道模型庫裡沒有這個上遊模型', '채널 모델 라이브러리에 없는 업스트림 모델이에요', 'Upstream model is not in the channel model library'),
  invalid_date: e('日期格式不合法', '날짜 형식이 올바르지 않아요', 'Invalid date'),
  invalid_patch: e('修改內容不合法', '변경 내용이 올바르지 않아요', 'Invalid change'),
  invalid_policy: e('自動選模策略不合法', '자동 선택 정책이 올바르지 않아요', 'Invalid auto-routing policy'),
  invalid_pricing: e('價目不合法', '가격 설정이 올바르지 않아요', 'Invalid pricing'),
  invalid_thinking_policy: e('思考策略不合法', '사고 정책이 올바르지 않아요', 'Invalid thinking policy'),
  kind_required: e('請先選擇模型類型', '모델 유형을 먼저 선택해 주세요', 'Select a model kind first'),
  price_not_scalar: e('這個模型的價目是複合結構，請到「已發布模型」裡編輯', '복합 가격 구조라서 「게시된 모델」에서 수정해 주세요', 'Compound pricing; edit it under Published models'),
  unroutable_models: e('策略裡有模型沒有可用路由', '정책에 라우트가 없는 모델이 있어요', 'The policy references models without usable routes'),
  refresh_cooldown: e('刷新太頻繁，請一分鐘後再試', '너무 자주 새로고침했어요. 1분 뒤에 다시 시도해 주세요', 'Refreshing too often; retry in a minute'),
  refresh_failed: e('公開刊例刷新失敗', '공개 가격표 갱신에 실패했어요', 'Failed to refresh public prices'),
  upstream_error: e('上遊返回了錯誤', '업스트림에서 오류가 반환됐어요', 'The upstream returned an error'),
  upstream_invalid_response: e('上遊返回的內容無法解析', '업스트림 응답을 해석할 수 없어요', 'The upstream response could not be parsed'),
  upstream_unreachable: e('連不上上遊，請檢查 Base URL 與網路', '업스트림에 연결할 수 없어요. Base URL과 네트워크를 확인해 주세요', 'Upstream unreachable; check the base URL and network'),
  invalid_api_key: e('上遊拒絕了這把 Key，什麼都沒有保存', '업스트림이 이 키를 거부했어요. 아무것도 저장하지 않았어요', 'The upstream rejected this key; nothing was saved'),
  channel_exists: e('這個渠道 ID 已經被佔用，換一個再試', '이미 쓰고 있는 채널 ID예요. 다른 ID로 다시 시도해 주세요', 'This channel ID is taken; try another'),
  invalid_channel_config: e('渠道配置不合法', '채널 설정이 올바르지 않아요', 'Invalid channel settings'),
}

/** 这些错误的原因只有服务端知道（哪一项不合法），附在文案后面方便定位。 */
const DETAILED = new Set(['invalid_policy', 'invalid_pricing', 'invalid_patch', 'upstream_error', 'refresh_failed', 'unroutable_models', 'invalid_channel_config'])

/** 本组页面的错误提示：先查本组新增的错误码，其余沿用共用错误表。 */
export function errorTextCat(err: unknown, locale: Locale): string {
  const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : ''
  const entry = CODES[code]
  if (!entry) return baseErrorText(err, locale)
  const detail = DETAILED.has(code) && err instanceof Error && err.message ? `: ${err.message}` : ''
  return translate(entry, locale) + detail
}

export const AG_catalog = {
  common: {
    loading: e('載入中...', '불러오는 중이에요...', 'Loading...'),
    loadFailed: e('載入失敗：{msg}', '불러오지 못했어요: {msg}', 'Failed to load: {msg}'),
    readOnly: e('當前帳號只有查看權限，修改需要管理員。', '현재 계정은 조회 권한만 있어요. 수정은 관리자만 할 수 있어요.', 'Your account is read-only; changes need an admin.'),
  },

  providers: {
    search: e('搜尋供應商', '공급자 검색', 'Search providers'),
    none: e('沒有匹配的供應商', '일치하는 공급자가 없어요', 'No matching providers'),
    add: e('新增供應商', '공급자 추가', 'Add provider'),
    pickOne: e('從左側選擇一個供應商查看詳情', '왼쪽에서 공급자를 선택해 상세를 확인해요', 'Select a provider on the left'),
    healthState: {
      healthy: e('正常', '정상', 'Healthy'),
      degraded: e('降級', '저하', 'Degraded'),
      open: e('已熔斷', '차단됨', 'Circuit open'),
      off: e('已停用', '비활성', 'Disabled'),
    },
    test: e('測試連通', '연결 테스트', 'Test connection'),
    testing: e('測試中...', '테스트 중...', 'Testing...'),
    testOk: e('連通，{ms}ms，上遊返回 {n} 個模型', '연결됨, {ms}ms, 업스트림 모델 {n}개', 'Connected, {ms}ms, {n} models'),
    testFail: e('不通：{err}', '연결 실패: {err}', 'Failed: {err}'),
    recover: e('恢復調度', '다시 투입', 'Recover'),
    recoverHint: e('清零連續失敗並解除自動熔斷，節點立即重新接收流量', '연속 실패를 초기화하고 차단을 풀어요. 노드가 바로 트래픽을 다시 받아요', 'Clear failures and lift the breaker so the node takes traffic immediately'),
    recovered: e('已恢復調度，配置版本 {v}', '다시 투입했어요. 설정 버전 {v}', 'Recovered, config version {v}'),
    fullSettings: e('完整設定', '전체 설정', 'Full settings'),
    window: e('近 {h} 小時', '최근 {h}시간', 'Last {h}h'),
    copyOff: e('這個供應商已停用，不會接收任何流量。', '이 공급자는 비활성 상태라 트래픽을 받지 않아요.', 'This provider is disabled and receives no traffic.'),
    copyOpen: e('連續失敗 {n} 次，已自動熔斷，冷卻到 {until}。原因：{reason}', '연속 {n}회 실패해 자동 차단됐어요. {until}까지 대기해요. 원인: {reason}', '{n} consecutive failures, breaker open until {until}. Reason: {reason}'),
    copyDegraded: e('連續失敗 {n} 次，達到 {max} 次會自動熔斷。', '연속 {n}회 실패했어요. {max}회가 되면 자동 차단돼요.', '{n} consecutive failures; the breaker opens at {max}.'),
    copyIdle: e('近 {h} 小時沒有經過這個供應商的請求。', '최근 {h}시간 동안 이 공급자를 거친 요청이 없어요.', 'No requests through this provider in the last {h}h.'),
    copyHealthy: e('近 {h} 小時運行正常，失敗 {failed} 次。', '최근 {h}시간 동안 정상이에요. 실패 {failed}건.', 'Healthy over the last {h}h, {failed} failures.'),
    mSuccess: e('成功率', '성공률', 'Success rate'),
    mRequests: e('請求數', '요청 수', 'Requests'),
    mP95: e('P95 延遲', 'P95 지연', 'P95 latency'),
    mFallback: e('降級切換', '폴백 전환', 'Fallback switches'),
    mRoutes: e('可用路由', '사용 가능 라우트', 'Active routes'),
    breakerTitle: e('自動熔斷', '자동 차단', 'Circuit breaker'),
    breakerCopy: e('連續失敗達到閾值後暫停調度，冷卻結束後自動試探恢復。', '연속 실패가 임계값에 닿으면 투입을 멈추고, 대기 후 자동으로 다시 시도해요.', 'Pause routing after consecutive failures, then probe again after the cooldown.'),
    breakerState: e('當前狀態', '현재 상태', 'State'),
    breakerManual: e('自動熔斷已關閉，只能手動停用', '자동 차단이 꺼져 있어요. 수동으로만 끌 수 있어요', 'Auto-breaker is off; disable manually'),
    breakerOpen: e('熔斷中，冷卻到 {until}', '차단 중이에요. {until}까지 대기', 'Open, cooling down until {until}'),
    breakerClosed: e('正常，連續失敗 {n} 次', '정상, 연속 실패 {n}회', 'Closed, {n} consecutive failures'),
    threshold: e('失敗閾值（次）', '실패 임계값(회)', 'Failure threshold'),
    cooldown: e('冷卻時間（秒）', '대기 시간(초)', 'Cooldown (s)'),
    saveBreaker: e('保存熔斷設定', '차단 설정 저장', 'Save breaker settings'),
    breakerSaved: e('熔斷設定已保存', '차단 설정을 저장했어요', 'Breaker settings saved'),
    limits: e('限額與狀態', '한도와 상태', 'Limits and status'),
    rpm: e('每分鐘請求上限', '분당 요청 한도', 'Requests per minute'),
    concurrent: e('並行上限', '동시 처리 한도', 'Concurrency limit'),
    enabled: e('啟用', '사용', 'Enabled'),
    enabledOn: e('已啟用', '사용으로 바꿨어요', 'Enabled'),
    enabledOff: e('已停用', '사용 안 함으로 바꿨어요', 'Disabled'),
    updatedAt: e('更新時間', '수정 시각', 'Updated'),
    options: e('節點參數', '노드 옵션', 'Node options'),
    noOptions: e('沒有設定節點參數', '설정된 노드 옵션이 없어요', 'No node options set'),
    optionsHint: e('節點參數在「完整設定」裡修改。', '노드 옵션은 「전체 설정」에서 수정해요.', 'Edit node options under Full settings.'),
    keyTitle: e('API Key', 'API 키', 'API key'),
    keyHint: e('Key 加密保存，只顯示末四位。填入新 Key 會立即替換。', '키는 암호화해 저장하고 끝 4자리만 보여줘요. 새 키를 입력하면 바로 교체돼요.', 'Keys are stored encrypted and only the last four characters are shown. A new key replaces it immediately.'),
    keyPlaceholder: e('填入新的上遊 Key', '새 업스트림 키 입력', 'Enter a new upstream key'),
    keyRotated: e('Key 已更新', '키를 교체했어요', 'Key updated'),
    save: e('保存', '저장', 'Save'),
    baseTitle: e('Base URL', 'Base URL', 'Base URL'),
    logoHint: e('填入圖片地址，在供應商列表與模型表裡顯示。留空使用首字母。', '이미지 주소를 입력하면 공급자 목록과 모델 표에 표시돼요. 비워 두면 첫 글자를 써요.', 'Image URL shown in the provider list and model table. Empty uses initials.'),
    logoSaved: e('Logo 已保存', '로고를 저장했어요', 'Logo saved'),
    modelsTitle: e('上遊模型', '업스트림 모델', 'Upstream models'),
    modelsFetched: e('上遊返回 {n} 個', '업스트림 {n}개', '{n} from upstream'),
    modelsRegistered: e('已登記 {n} 個', '등록됨 {n}개', '{n} registered'),
    fetchModels: e('拉取模型清單', '모델 목록 가져오기', 'Fetch model list'),
    fetching: e('拉取中...', '가져오는 중...', 'Fetching...'),
    fetchEmpty: e('點選「拉取模型清單」從上遊讀取', '「모델 목록 가져오기」를 눌러 업스트림에서 읽어와요', 'Click Fetch model list to read from the upstream'),
    fetchNone: e('上遊沒有返回模型', '업스트림이 모델을 반환하지 않았어요', 'The upstream returned no models'),
    fetchFoot: e('登記模型請到「渠道模型庫」。', '모델 등록은 「채널 모델 라이브러리」에서 해요.', 'Register models in the Channel model library.'),
    registered: e('已登記', '등록됨', 'Registered'),
    unregistered: e('未登記', '미등록', 'Not registered'),
    delete: e('刪除供應商', '공급자 삭제', 'Delete provider'),
    confirmDelete: e('刪除供應商「{name}」', '공급자 「{name}」 삭제', 'Delete provider "{name}"'),
    deleteHint: e('只有沒有路由和已登記模型引用的供應商才能刪除。', '라우트와 등록된 모델이 없는 공급자만 삭제할 수 있어요.', 'Only providers with no routes or registered models can be deleted.'),
    deleted: e('供應商已刪除', '공급자를 삭제했어요', 'Provider deleted'),
    savedVersion: e('已保存，配置版本 {v}', '저장했어요. 설정 버전 {v}', 'Saved, config version {v}'),
  },

  presets: {
    title: e('新增供應商', '공급자 추가', 'Add provider'),
    search: e('搜尋供應商或地址', '공급자나 주소 검색', 'Search providers or URLs'),
    none: e('沒有匹配的預置供應商', '일치하는 기본 공급자가 없어요', 'No matching presets'),
    category: {
      all: e('全部', '전체', 'All'),
      global: e('海外', '해외', 'Global'),
      china: e('國內', '중국', 'China'),
      coding: e('編程套餐', '코딩 플랜', 'Coding plans'),
      platform: e('聚合平臺', '통합 플랫폼', 'Aggregators'),
    },
    installed: e('已添加 {n}', '추가됨 {n}', 'Added {n}'),
    custom: e('列表裡沒有？自定義供應商', '목록에 없나요? 직접 설정하기', 'Not listed? Configure a custom provider'),
    back: e('返回列表', '목록으로', 'Back to list'),
    docs: e('介面文件', 'API 문서', 'API docs'),
    keyLabel: e('API Key', 'API 키', 'API key'),
    keyHint: e('填好後會先用這把 Key 請求上遊模型清單，連通才保存並啟用。', '입력하면 먼저 이 키로 업스트림 모델 목록을 요청하고, 연결되면 저장하고 바로 사용해요.', 'The key is tested against the upstream model list first; the provider is saved and enabled only if it connects.'),
    idLabel: e('渠道 ID', '채널 ID', 'Channel ID'),
    idHint: e('同一家裝第二把 Key 時換一個 ID，建立後不能改。', '같은 공급자에 키를 하나 더 넣을 때는 다른 ID를 써요. 만든 뒤에는 바꿀 수 없어요.', 'Use another ID for a second key of the same provider. It cannot be changed later.'),
    nameLabel: e('顯示名稱', '표시 이름', 'Display name'),
    advanced: e('更換 Base URL（區域端點或私有部署）', 'Base URL 바꾸기(리전 엔드포인트, 프라이빗 배포)', 'Change base URL (regional or private endpoint)'),
    protocol: e('協議 {p}', '프로토콜 {p}', 'Protocol {p}'),
    presetOptions: e('已按這家上遊的特點預填節點參數：{list}', '이 업스트림에 맞춰 노드 옵션을 미리 채웠어요: {list}', 'Node options prefilled for this upstream: {list}'),
    install: e('測試並上線', '테스트하고 사용하기', 'Test and go live'),
    installing: e('正在連通上遊...', '업스트림에 연결하는 중...', 'Connecting to upstream...'),
    done: e('{name} 已上線，登記了 {n} 個上遊模型，耗時 {ms}ms。', '{name} 사용을 시작했어요. 업스트림 모델 {n}개를 등록했어요({ms}ms).', '{name} is live with {n} upstream models registered ({ms}ms).'),
    doneNoList: e('{name} 已上線。這家上遊沒有模型清單介面，請到「渠道模型庫」手動登記模型。', '{name} 사용을 시작했어요. 이 업스트림은 모델 목록 API가 없어서 「채널 모델 라이브러리」에서 직접 등록해야 해요.', '{name} is live. This upstream has no model list endpoint, so register models in the Channel model library.'),
    publishNext: e('對使用者開放模型請到「已發布模型」。', '사용자에게 모델을 공개하려면 「게시된 모델」로 가요.', 'Publish models to users under Published models.'),
  },

  library: {
    note: e(
      '渠道模型庫是採購側的唯一事實來源：每個渠道可用的上遊模型都先在這裡登記刊例價與折扣，採購價 = 刊例價 × 廠商折扣率。沒有報價的模型成本記為未知，不按 0 計。路由只能引用這裡已登記且啟用的模型。',
      '채널 모델 라이브러리는 매입 측의 단일 기준이에요. 채널별 업스트림 모델의 정가와 할인율을 여기서 등록하고, 매입가 = 정가 × 공급사 할인율이에요. 견적이 없는 모델의 원가는 미확인으로 처리하고 0으로 계산하지 않아요. 라우트는 여기에 등록되고 활성화된 모델만 참조할 수 있어요.',
      'The channel model library is the single source for cost: register list price and discount per channel; cost = list price x vendor discount. Models without a quote have unknown cost, which is never counted as 0. Routes can only reference models registered and enabled here.',
    ),
    sTotal: e('已登記模型', '등록된 모델', 'Registered models'),
    sTotalHint: e('全部渠道', '전체 채널', 'All channels'),
    sPriced: e('已有報價', '견적 있음', 'Quoted'),
    sPricedHint: e('啟用且成本已知', '활성이며 원가 확인됨', 'Enabled with known cost'),
    sMissing: e('缺報價', '견적 없음', 'Missing quote'),
    sMissingHint: e('啟用但成本未知', '활성이지만 원가 미확인', 'Enabled but cost unknown'),
    sUnknown: e('成本未知的呼叫', '원가 미확인 호출', 'Unknown-cost calls'),
    sUnknownHint: e('近 30 天', '최근 30일', 'Last 30 days'),
    search: e('搜尋上遊模型、渠道、備註', '업스트림 모델, 채널, 메모 검색', 'Search model, channel, note'),
    allChannels: e('全部渠道', '전체 채널', 'All channels'),
    missingOnly: e('只看缺報價', '견적 없는 것만', 'Missing quote only'),
    refreshRefs: e('刷新公開刊例', '공개 가격표 갱신', 'Refresh public prices'),
    refsRefreshed: e('已刷新公開刊例，共 {n} 個模型', '공개 가격표를 갱신했어요. 모델 {n}개', 'Public prices refreshed, {n} models'),
    refsNever: e('公開刊例還沒有刷新過', '공개 가격표를 아직 갱신하지 않았어요', 'Public prices never refreshed'),
    refsError: e('上次刷新失敗：{err}', '지난 갱신 실패: {err}', 'Last refresh failed: {err}'),
    refsStatus: e('公開刊例 {n} 個模型，更新於 {t}', '공개 가격표 {n}개 모델, {t} 갱신', 'Public prices: {n} models, updated {t}'),
    refsTitle: e('公開刊例（OpenRouter）', '공개 가격표(OpenRouter)', 'Public prices (OpenRouter)'),
    refsNone: e('沒有找到相近的公開刊例', '비슷한 공개 가격표가 없어요', 'No similar public prices found'),
    refsPrice: e('輸入 {p} / 輸出 {c}（每百萬 Token）', '입력 {p} / 출력 {c} (100만 토큰당)', 'in {p} / out {c} (per 1M tokens)'),
    refsFill: e('填入', '채우기', 'Use'),
    register: e('登記模型', '모델 등록', 'Register model'),
    colModel: e('上遊模型', '업스트림 모델', 'Upstream model'),
    colKind: e('類型', '유형', 'Kind'),
    colQuote: e('報價（刊例價）', '견적(정가)', 'Quote (list)'),
    colCalls: e('近 30 天呼叫', '30일 호출', 'Calls, 30d'),
    colUnknown: e('成本未知', '원가 미확인', 'Unknown cost'),
    colUnknownHint: e('近 30 天裡成本未知的呼叫數', '최근 30일 원가를 알 수 없는 호출 수', 'Calls with unknown cost in 30 days'),
    colRefs: e('路由引用', '라우트 참조', 'Route refs'),
    colRefsHint: e('被多少條路由引用', '몇 개의 라우트가 참조하는지', 'How many routes reference it'),
    colSource: e('來源', '출처', 'Source'),
    colStatus: e('狀態', '상태', 'Status'),
    colActions: e('操作', '작업', 'Actions'),
    empty: e('沒有匹配的模型', '일치하는 모델이 없어요', 'No matching models'),
    source: {
      manual: e('手動登記', '수동 등록', 'Manual'),
      fetched: e('上遊拉取', '업스트림 수집', 'Fetched'),
      published: e('發布時建立', '게시 시 생성', 'Published'),
      migrated: e('遷移', '마이그레이션', 'Migrated'),
    },
    editQuote: e('改報價', '견적 수정', 'Edit quote'),
    fullEdit: e('完整編輯', '전체 편집', 'Full edit'),
    enable: e('啟用', '활성화', 'Enable'),
    disable: e('停用', '비활성화', 'Disable'),
    toggled: e('已切換，配置版本 {v}', '변경했어요. 설정 버전 {v}', 'Updated, config version {v}'),
    footer: e('缺報價的行整行標紅。折扣為 0 表示合作免費，算成本已知。', '견적이 없는 행은 빨간색으로 표시돼요. 할인율 0은 무상 제휴로 보고 원가 확인으로 쳐요.', 'Rows without a quote are red. Discount 0 means free and counts as known cost.'),
    quoteTitle: e('報價：{name}', '견적: {name}', 'Quote: {name}'),
    quoteLead: e('填刊例價（原幣種、每百萬 Token）與廠商折扣率。', '정가(원래 통화, 100만 토큰당)와 공급사 할인율을 입력해요.', 'Enter list price (original currency, per 1M tokens) and the vendor discount.'),
    quoteComplex: e('這個模型有分時、圖片 Token 或影片等複合價目，行內只能改簡單報價，請用「完整編輯」。', '시간대별, 이미지 토큰, 영상 등 복합 가격이라 여기서는 단순 견적만 고칠 수 있어요. 「전체 편집」을 사용해 주세요.', 'This model has compound pricing; use Full edit.'),
    regTitle: e('登記模型', '모델 등록', 'Register model'),
    regLead: e('選渠道後會自動讀取該渠道的模型目錄作為候選，讀取失敗不影響手填。', '채널을 고르면 해당 채널의 모델 목록을 후보로 불러와요. 실패해도 직접 입력할 수 있어요.', 'Choosing a channel loads its model list as candidates; manual entry still works if that fails.'),
    regSubmit: e('登記', '등록', 'Register'),
    regFetching: e('正在讀取渠道模型目錄...', '채널 모델 목록을 읽는 중...', 'Loading channel models...'),
    regFetched: e('讀到 {n} 個候選', '후보 {n}개를 읽었어요', '{n} candidates loaded'),
    regFetchFailed: e('讀取失敗：{msg}', '읽지 못했어요: {msg}', 'Could not load: {msg}'),
  },

  models: {
    sPublished: e('已發布模型', '게시된 모델', 'Published models'),
    sPublishedHint: e('其中啟用 {n} 個', '활성 {n}개', '{n} enabled'),
    sCoverage: e('路由覆蓋率', '라우트 커버리지', 'Route coverage'),
    sCoverageHint: e('{a} / {b} 個模型至少有一個可用節點', '모델 {b}개 중 {a}개에 사용 가능한 노드가 있어요', '{a} of {b} models have a usable node'),
    sNodes: e('可用路由節點', '사용 가능한 노드', 'Healthy nodes'),
    sNodesHint: e('啟用且渠道未熔斷 / 全部啟用節點', '활성이며 채널이 차단되지 않음 / 전체 활성 노드', 'Healthy / all enabled nodes'),
    sFallback: e('近 30 分鐘降級切換', '30분 폴백 전환', 'Fallbacks, 30 min'),
    sFallbackHint: e('全部節點失敗 {n} 次', '모든 노드 실패 {n}회', 'All nodes failed {n} times'),
    fbTitle: e('降級監控', '폴백 모니터링', 'Fallback monitoring'),
    fbNote: e('當前層節點失敗時自動切到下一層；全部層都失敗才會返回錯誤並告警。', '현재 층의 노드가 실패하면 다음 층으로 자동 전환해요. 모든 층이 실패해야 오류를 반환하고 알림을 보내요.', 'When a layer fails, traffic moves to the next; only when all layers fail does the call error and alert.'),
    fbAlert: e('告警電子信箱：{mail}；Webhook：{hook}；規則：{w} 分鐘內全部失敗 {n} 次', '알림 이메일: {mail}, 웹훅: {hook}, 규칙: {w}분 안에 전체 실패 {n}회', 'Alert email: {mail}; webhook: {hook}; rule: {n} total failures within {w} min'),
    fbOn: e('已配置', '설정됨', 'configured'),
    fbOff: e('未配置', '미설정', 'not set'),
    fbChip: e('切換 {a} / 全失敗 {b}', '전환 {a} / 전체 실패 {b}', 'switch {a} / all failed {b}'),
    fbEvAll: e('全部節點失敗', '모든 노드 실패', 'all nodes failed'),
    fbEvSwitch: e('{a} 切到 {b}', '{a} → {b} 전환', '{a} switched to {b}'),
    bannerInverted: e('賣價低於採購價（倒掛）的模型 {n} 個：', '판매가가 매입가보다 낮은(역마진) 모델 {n}개:', '{n} models are priced below cost:'),
    bannerZero: e('賣價等於採購價（零毛利）的模型 {n} 個：', '판매가와 매입가가 같은(마진 0) 모델 {n}개:', '{n} models have zero margin:'),
    bannerMissing: e('有 {n} 個模型的路由節點缺採購價，成本按未知處理：', '매입가가 없는 라우트 노드가 있는 모델 {n}개, 원가는 미확인으로 처리돼요:', '{n} models have route nodes without a cost quote (cost unknown):'),
    search: e('搜尋模型 ID、顯示名、標籤', '모델 ID, 표시 이름, 태그 검색', 'Search ID, name, tag'),
    allKinds: e('全部類型', '전체 유형', 'All kinds'),
    allVisibility: e('全部可見性', '전체 공개 범위', 'All visibility'),
    visOnlyPublic: e('公開', '공개', 'Public'),
    visOnlyWhitelist: e('白名單', '화이트리스트', 'Whitelist'),
    visOnlyHidden: e('不公開', '비공개', 'Hidden'),
    count: e('{a} / {b} 個', '{a} / {b}개', '{a} of {b}'),
    reprice: e('按採購價重定價', '매입가 기준 재가격', 'Reprice from cost'),
    publish: e('發布模型', '모델 게시', 'Publish models'),
    colModel: e('模型', '모델', 'Model'),
    colRoutes: e('降級鏈', '폴백 체인', 'Routes'),
    colCost: e('採購價（USD / 百萬 Token）', '매입가(USD / 100만 토큰)', 'Cost (USD / 1M tokens)'),
    colCostHint: e('取主路由層裡最貴的節點，× 為賣價相對採購價的倍率', '주 라우트 층에서 가장 비싼 노드 기준이에요. ×는 판매가 대비 매입가 배율이에요', 'Highest-cost node in the primary layer; x is sell / cost'),
    colContext: e('上下文', '컨텍스트', 'Context'),
    colVisibility: e('可見性', '공개 범위', 'Visibility'),
    routeSummary: e('{a} / {b} 個節點', '노드 {a} / {b}', '{a} / {b} nodes'),
    costNone: {
      unknown: e('成本未知', '원가 미확인', 'Unknown cost'),
      skipped: e('含分時或階梯價，未對照', '시간대/구간 가격이라 비교하지 않았어요', 'Tiered or peak pricing, not compared'),
      na: e('無對照', '비교 대상 없음', 'N/A'),
    } as Record<string, Entry>,
    costMissingRoutes: e('{n} 個節點缺採購價', '매입가 없는 노드 {n}개', '{n} nodes without cost'),
    visWhitelistN: e('白名單 {n} 個帳號', '화이트리스트 계정 {n}곳', 'Whitelist, {n} accounts'),
    visHidden: e('不公開', '비공개', 'Hidden'),
    menuVisibility: e('可見性與標籤', '공개 범위와 태그', 'Visibility and tags'),
    copy: e('複製模型', '모델 복사', 'Copy model'),
    copyTitle: e('複製模型：{id}', '모델 복사: {id}', 'Copy model: {id}'),
    copyLead: e('價目、思考策略和路由一併複製，新模型預設停用，核對後再啟用。', '가격, 사고 정책, 라우트를 함께 복사해요. 새 모델은 기본으로 비활성이라 확인 후 활성화해 주세요.', 'Pricing, thinking policy and routes are copied. The copy starts disabled; enable it after review.'),
    copied: e('已複製為 {id}，配置版本 {v}', '{id}(으)로 복사했어요. 설정 버전 {v}', 'Copied as {id}, config version {v}'),
    deleteTitle: e('刪除模型「{id}」', '모델 「{id}」 삭제', 'Delete model "{id}"'),
    deleteHint: e('同時刪除它的全部路由，呼叫這個模型的請求會失敗。', '라우트도 모두 삭제돼요. 이 모델을 호출하는 요청은 실패해요.', 'All its routes are deleted too; requests for this model will fail.'),
    deleted: e('模型已刪除，配置版本 {v}', '모델을 삭제했어요. 설정 버전 {v}', 'Model deleted, config version {v}'),
    empty: e('沒有匹配的模型', '일치하는 모델이 없어요', 'No matching models'),
    visTitle: e('可見性與標籤：{id}', '공개 범위와 태그: {id}', 'Visibility and tags: {id}'),
    visLead: e('不公開的模型不出現在 /v1/models，只有白名單帳號能看到；呼叫權限不受這個開關影響。', '비공개 모델은 /v1/models에 나오지 않고 화이트리스트 계정에게만 보여요. 호출 권한은 이 설정과 무관해요.', 'Hidden models are absent from /v1/models except for whitelisted accounts; call permission is unaffected.'),
    visPublic: e('公開（所有人在 /v1/models 裡可見）', '공개(모든 사용자의 /v1/models에 표시)', 'Public (listed for everyone)'),
    visOrgs: e('白名單帳號 ID', '화이트리스트 계정 ID', 'Whitelisted account IDs'),
    visOrgsHint: e('每行一個 UUID，也可以用逗號分隔', '한 줄에 UUID 하나, 쉼표로 구분해도 돼요', 'One UUID per line or comma separated'),
    visTags: e('標籤', '태그', 'Tags'),
    visTagsHint: e('用逗號分隔，例如：推理, 長上下文', '쉼표로 구분해요. 예: 추론, 긴 컨텍스트', 'Comma separated, e.g. reasoning, long-context'),
    visIcon: e('圖標地址', '아이콘 주소', 'Icon URL'),
    badOrg: e('帳號 ID 格式有誤：{v}', '계정 ID 형식이 올바르지 않아요: {v}', 'Malformed account ID: {v}'),
    repriceTitle: e('按採購價重定價', '매입가 기준 재가격', 'Reprice from cost'),
    repriceLead: e('賣價 = 主路由採購價 × 倍率，覆蓋當前篩選出的 {n} 個模型。先預覽，確認後才寫庫。', '판매가 = 주 라우트 매입가 × 배율이에요. 현재 필터된 모델 {n}개에 적용해요. 먼저 미리보고 확인 후 저장돼요.', 'Sell = primary-route cost x markup, for the {n} filtered models. Preview first; nothing is saved until you apply.'),
    repriceMarkup: e('倍率', '배율', 'Markup'),
    repriceMarkupHint: e('1.3 表示在採購價上加價 30%', '1.3은 매입가에 30%를 더한다는 뜻이에요', '1.3 means cost plus 30%'),
    repriceDry: e('預覽', '미리보기', 'Preview'),
    repriceApply: e('應用到 {n} 個模型', '모델 {n}개에 적용', 'Apply to {n} models'),
    repriceCost: e('採購價（USD）', '매입가(USD)', 'Cost (USD)'),
    repriceResult: e('結果', '결과', 'Result'),
    repriceReady: e('可應用', '적용 가능', 'Ready'),
    repriceApplied: e('已應用', '적용됨', 'Applied'),
    repriceSkip: {
      unknown: e('跳過：成本未知', '건너뜀: 원가 미확인', 'Skipped: unknown cost'),
      skipped: e('跳過：含分時、限時或階梯價', '건너뜀: 시간대/한정/구간 가격', 'Skipped: tiered, peak or promo pricing'),
      na: e('跳過：沒有路由或類型不支援', '건너뜀: 라우트 없음 또는 미지원 유형', 'Skipped: no routes or unsupported kind'),
      gone: e('跳過：模型已不存在', '건너뜀: 모델이 없어졌어요', 'Skipped: model is gone'),
    } as Record<string, Entry>,
    repriceDone: e('已重定價 {n} 個模型，配置版本 {v}', '모델 {n}개를 재가격했어요. 설정 버전 {v}', 'Repriced {n} models, config version {v}'),
    publishTitle: e('發布模型', '모델 게시', 'Publish models'),
    publishLead: e('這裡列出渠道模型庫裡已啟用、還沒有被任何路由引用的上遊模型。選中後按倍率定價並建成對外模型；沒有採購價的會建成停用狀態，補齊報價後再啟用。', '채널 모델 라이브러리에서 활성화됐고 아직 어떤 라우트도 참조하지 않는 업스트림 모델이에요. 선택하면 배율로 가격을 정해 공개 모델로 만들어요. 매입가가 없으면 비활성으로 생성되니 견적을 채운 뒤 활성화해 주세요.', 'Enabled upstream models in the library that no route references yet. Selected ones are priced by markup and created as public models; those without a cost are created disabled until quoted.'),
    publishFilter: e('篩選', '필터', 'Filter'),
    publishMarkupHint: e('賣價 = 採購價 × 倍率', '판매가 = 매입가 × 배율', 'Sell = cost x markup'),
    publishUpstream: e('上遊模型', '업스트림 모델', 'Upstream model'),
    publishNoCost: e('缺採購價', '매입가 없음', 'no cost'),
    publishEmpty: e('沒有可發布的模型', '게시할 모델이 없어요', 'Nothing to publish'),
    publishPicked: e('已選 {n} 個', '{n}개 선택', '{n} selected'),
    publishSubmit: e('發布 {n} 個', '{n}개 게시', 'Publish {n}'),
    publishDone: e('已發布 {n} 個模型', '모델 {n}개를 게시했어요', 'Published {n} models'),
    publishCreated: e('已建立', '생성됨', 'Created'),
    publishNeedsPricing: e('已建立，缺採購價，處於停用', '생성됨, 매입가 없음, 비활성 상태', 'Created disabled, needs pricing'),
    publishExists: e('對外 ID 已存在', '같은 공개 ID가 이미 있어요', 'ID already exists'),
    wbExplain1: e('優先級數字越小越先用', '우선순위 숫자가 작을수록 먼저 써요', 'Lower priority number goes first'),
    wbExplain2: e('同一層內按權重分流', '같은 층 안에서는 가중치로 분배해요', 'Same layer splits by weight'),
    wbExplain3: e('當前層全部失敗才落到下一層', '현재 층이 모두 실패하면 다음 층으로 가요', 'Next layer only when this one fails'),
    wbLane: e('第 {n} 層', '{n}층', 'Layer {n}'),
    wbLaneSub: e('優先級 {p}', '우선순위 {p}', 'priority {p}'),
    wbPriority: e('優先級', '우선순위', 'Priority'),
    wbWeight: e('權重', '가중치', 'Weight'),
    wbRemove: e('移除節點', '노드 제거', 'Remove node'),
    wbOverrides: e('節點參數 {n} 項', '노드 파라미터 {n}개', '{n} node overrides'),
    wbAdd: e('添加節點', '노드 추가', 'Add node'),
    wbNoUpstream: e('渠道模型庫裡沒有可選的同類型模型', '라이브러리에 같은 유형의 선택 가능한 모델이 없어요', 'No matching models in the library'),
    wbDuplicate: e('這個節點已經在降級鏈裡', '이미 폴백 체인에 있는 노드예요', 'This node is already in the chain'),
    wbBadPriority: e('優先級要填 0 到 100 的整數', '우선순위는 0~100 정수로 입력해 주세요', 'Priority must be an integer 0 to 100'),
    wbBadWeight: e('權重要填 1 到 10000 的整數', '가중치는 1~10000 정수로 입력해 주세요', 'Weight must be an integer 1 to 10000'),
    wbAdvanced: e('高級（節點參數）', '고급(노드 파라미터)', 'Advanced (node params)'),
    wbSave: e('保存路由', '라우트 저장', 'Save routes'),
    wbSaved: e('路由已保存，配置版本 {v}', '라우트를 저장했어요. 설정 버전 {v}', 'Routes saved, config version {v}'),
  },

  autoRouting: {
    desc: e('按請求內容自動選擇模型：簡單問題走經濟檔，複雜問題走品質檔', '요청 내용에 따라 모델을 자동 선택해요. 간단한 질문은 경제 등급, 복잡한 질문은 품질 등급이에요', 'Pick a model by request content: simple prompts go economy, complex ones go quality'),
    chip: e('model 填 auto 即可使用', 'model에 auto를 쓰면 돼요', 'Use model "auto"'),
    sPolicy: e('當前策略', '현재 정책', 'Policy'),
    sourceSaved: e('已發布', '게시됨', 'Published'),
    sourceDefault: e('預設模板', '기본 템플릿', 'Default template'),
    notSaved: e('尚未保存', '아직 저장 안 함', 'Not saved yet'),
    sScenes: e('場景數', '시나리오 수', 'Scenes'),
    sScenesHint: e('生效 {a} 個，影子 {b} 個', '적용 {a}개, 섀도 {b}개', '{a} enforce, {b} shadow'),
    sRequests: e('自動選模請求', '자동 선택 요청', 'Auto-routed requests'),
    sRequestsHint: e('近 {h} 小時', '최근 {h}시간', 'Last {h}h'),
    sShadow: e('影子決策數', '섀도 결정 수', 'Shadow decisions'),
    sShadowHint: e('近 {h} 小時', '최근 {h}시간', 'Last {h}h'),
    note: e(
      '呼叫方把 model 寫成 auto 時，資料面按場景策略選出真實模型。規則按順序匹配，命中即停；都不命中走場景預設檔。同一會話選過品質檔後會綁定一段時間，避免對話中途換模型，修改 binding_epoch 可以讓全部綁定作廢。場景用請求頭 X-Route-Scene 指定，會話用 X-Conversation-Id。',
      '호출자가 model을 auto로 쓰면 데이터 플레인이 시나리오 정책으로 실제 모델을 골라요. 규칙은 순서대로 맞춰 보고 처음 맞는 것에서 멈추고, 아무것도 맞지 않으면 시나리오 기본 등급을 써요. 한 대화에서 품질 등급이 선택되면 일정 시간 고정돼 대화 도중 모델이 바뀌지 않아요. binding_epoch를 바꾸면 모든 고정이 무효가 돼요. 시나리오는 X-Route-Scene 헤더, 대화는 X-Conversation-Id 헤더로 지정해요.',
      'When a caller sets model to auto, the data plane picks a real model from the scene policy. Rules match in order and stop at the first hit, otherwise the scene default applies. Once a conversation gets the quality tier it stays bound for a while; bump binding_epoch to void all bindings. Scene via X-Route-Scene, conversation via X-Conversation-Id.',
    ),
    available: e('可路由的對話模型：', '라우팅 가능한 대화 모델:', 'Routable chat models:'),
    editorTitle: e('策略編輯器', '정책 편집기', 'Policy editor'),
    editorHint: e('發布前先「校驗與試算」', '게시 전에 「검증 및 시뮬레이션」을 먼저 해요', 'Validate and simulate before publishing'),
    reset: e('還原為已保存', '저장본으로 되돌리기', 'Revert to saved'),
    preview: e('校驗與試算', '검증 및 시뮬레이션', 'Validate and simulate'),
    publish: e('發布策略', '정책 게시', 'Publish policy'),
    published: e('策略已發布，配置版本 {v}', '정책을 게시했어요. 설정 버전 {v}', 'Policy published, config version {v}'),
    badJson: e('策略的 JSON 格式有誤', '정책의 JSON 형식이 올바르지 않아요', 'The policy JSON is malformed'),
    samples: e('試算樣本（多個樣本之間用單獨一行 --- 分隔）', '시뮬레이션 샘플(샘플 사이는 --- 한 줄로 구분)', 'Simulation samples (separate with a line of ---)'),
    previewOk: e('策略合法，下表是每個樣本在每個場景裡會選到的模型。試算不寫庫。', '정책이 올바르고, 아래는 시나리오별로 각 샘플이 선택할 모델이에요. 시뮬레이션은 저장하지 않아요.', 'The policy is valid. The table shows which model each sample gets per scene. Nothing is saved.'),
    previewFail: e('策略不合法：{err}', '정책이 올바르지 않아요: {err}', 'Invalid policy: {err}'),
    unroutable: e('這些模型沒有可用路由，呼叫時會回落到備用模型：', '라우트가 없는 모델이에요. 호출 시 예비 모델로 대체돼요:', 'No usable routes for these; calls fall back:'),
    colScene: e('場景', '시나리오', 'Scene'),
    colSample: e('樣本', '샘플', 'Sample'),
    colLevel: e('檔位', '등급', 'Tier'),
    colModel: e('選到的模型', '선택된 모델', 'Chosen model'),
    colReason: e('原因', '이유', 'Reason'),
    colMode: e('模式', '모드', 'Mode'),
    levelEconomy: e('經濟', '경제', 'Economy'),
    levelQuality: e('品質', '품질', 'Quality'),
    actual: e('實際用 {m}', '실제로는 {m}', 'actually {m}'),
    reason: {
      fixed: e('固定模型', '고정 모델', 'Fixed model'),
      default: e('場景預設檔', '시나리오 기본 등급', 'Scene default'),
      sticky: e('會話已綁定品質檔', '대화가 품질 등급에 고정됨', 'Sticky quality binding'),
    } as Record<string, Entry>,
    ruleHit: e('命中規則 {id}', '규칙 {id} 일치', 'Matched rule {id}'),
    reasonSfx: {
      shadow: e('影子', '섀도', 'shadow'),
      fallback: e('回落備用模型', '예비 모델로 대체', 'fell back'),
      unroutable: e('無可用路由', '사용 가능한 라우트 없음', 'unroutable'),
    } as Record<string, Entry>,
    statsTitle: e('路由統計', '라우팅 통계', 'Routing stats'),
    hoursOpt: e('近 {h} 小時', '최근 {h}시간', 'Last {h}h'),
    refresh: e('刷新', '새로고침', 'Refresh'),
    colResolved: e('實際模型', '실제 모델', 'Resolved model'),
    colRequests: e('請求數', '요청 수', 'Requests'),
    colDecision: e('平均決策耗時', '평균 결정 시간', 'Avg decision time'),
    statsEmpty: e('這段時間沒有自動選模請求', '이 기간에 자동 선택 요청이 없어요', 'No auto-routed requests in this window'),
    shadowTitle: e('影子決策', '섀도 결정', 'Shadow decisions'),
    shadowHint: e('候選規則並行計算，只記錄對比，不影響呼叫', '후보 규칙을 병렬로 계산해 비교만 기록하고 호출에는 영향을 주지 않아요', 'Candidate rules run in parallel and are only recorded'),
    cfgEnabled: e('啟用影子', '섀도 사용', 'Enable shadow'),
    cfgRate: e('採樣率（0 到 1）', '샘플링 비율(0~1)', 'Sample rate (0 to 1)'),
    cfgSaved: e('影子採樣已保存，配置版本 {v}', '섀도 샘플링을 저장했어요. 설정 버전 {v}', 'Shadow sampling saved, config version {v}'),
    cfgNeedPolicy: e('先發布一份策略才能改影子採樣', '정책을 먼저 게시해야 섀도 샘플링을 바꿀 수 있어요', 'Publish a policy first'),
    colEvents: e('樣本數', '샘플 수', 'Events'),
    colSuccess: e('影子成功率', '섀도 성공률', 'Shadow success'),
    colAgreement: e('與生效一致率', '적용 결과와 일치율', 'Agreement'),
    colPrimary: e('生效決策 P50 / P95', '적용 결정 P50 / P95', 'Primary P50 / P95'),
    colShadowLat: e('影子決策 P50 / P95', '섀도 결정 P50 / P95', 'Shadow P50 / P95'),
    colCross: e('檔位對照（生效 → 影子）', '등급 비교(적용 → 섀도)', 'Tier matrix (primary to shadow)'),
    shadowEmpty: e('這段時間沒有影子決策記錄', '이 기간에 섀도 결정 기록이 없어요', 'No shadow records in this window'),
  },

  capabilities: {
    desc: e('生圖、影片、語音、向量、轉寫、音樂等按次或按量計費能力的呼叫與收入', '이미지, 영상, 음성, 임베딩, 전사, 음악 등 건당/사용량 과금 기능의 호출과 수익', 'Usage and revenue of per-call and per-unit capabilities such as image, video, speech, embeddings, transcription and music'),
    chip: e('失敗呼叫不扣費', '실패한 호출은 과금하지 않아요', 'Failed calls are not billed'),
    sRequests: e('呼叫次數', '호출 수', 'Calls'),
    sRequestsSub: e('今日 {n} 次', '오늘 {n}회', '{n} today'),
    sOrgs: e('呼叫帳號數', '호출 계정 수', 'Calling accounts'),
    sOrgsSub: e('每個帳號 {n} 次', '계정당 {n}회', '{n} per account'),
    sRevenue: e('收入', '수익', 'Revenue'),
    sRevenueSub: e('每次 {v}', '건당 {v}', '{v} per call'),
    sFailures: e('失敗次數', '실패 수', 'Failures'),
    sFailuresSub: e('上遊異常、參數錯誤、無結果', '업스트림 오류, 파라미터 오류, 결과 없음', 'Upstream errors, bad params, empty results'),
    total: e('共 {n} 條流水', '내역 {n}건', '{n} records'),
    to: e('至', '~', 'to'),
    today: e('今天', '오늘', 'Today'),
    d7: e('近 7 天', '최근 7일', 'Last 7 days'),
    d30: e('近 30 天', '최근 30일', 'Last 30 days'),
    clear: e('清空', '초기화', 'Clear'),
    note: e(
      '日期按自然日雙側包含，留空為全部歷史。「今日呼叫」始終按當天統計，不受日期篩選影響。收入來自成功扣費流水，失敗次數取自請求日誌，失敗不產生帳單。單價可以直接在表裡修改，保存後立即對新呼叫生效，歷史流水按當時的價格結算，不會被追溯改寫。',
      '날짜는 양쪽 모두 포함하는 하루 단위이고, 비워 두면 전체 기간이에요. 「오늘 호출」은 항상 당일 기준이며 날짜 필터의 영향을 받지 않아요. 수익은 성공 과금 내역, 실패 수는 요청 로그에서 가져오고 실패는 청구되지 않아요. 단가는 표에서 바로 수정할 수 있고 저장 즉시 새 호출에 적용돼요. 과거 내역은 당시 가격으로 정산돼 소급 변경되지 않아요.',
      'Dates are inclusive calendar days; empty means all history. Today is always counted for the current day. Revenue comes from successful billing records and failures from request logs; failures are not billed. Unit prices can be edited in the table and apply to new calls immediately; past records keep the price at the time.',
    ),
    colCap: e('能力', '기능', 'Capability'),
    colPrice: e('單價', '단가', 'Unit price'),
    colRequests: e('呼叫次數', '호출 수', 'Calls'),
    colOrgs: e('帳號數', '계정 수', 'Accounts'),
    colRevenue: e('收入', '수익', 'Revenue'),
    colCost: e('上遊成本', '업스트림 원가', 'Upstream cost'),
    colFailures: e('失敗次數', '실패 수', 'Failures'),
    colShare: e('佔比', '비중', 'Share'),
    colLast: e('最近呼叫', '최근 호출', 'Last call'),
    unit: {
      per_image: e('每張', '장당', 'per image'),
      per_million_tokens: e('每百萬 Token', '100만 토큰당', 'per 1M tokens'),
      per_million_chars: e('每百萬字元', '100만 자당', 'per 1M chars'),
      per_million_seconds: e('每百萬秒', '100만 초당', 'per 1M seconds'),
      per_minute: e('每分鐘', '분당', 'per minute'),
    } as Record<string, Entry>,
    editPrice: e('修改單價', '단가 수정', 'Edit price'),
    priceSaved: e('單價已保存，配置版本 {v}', '단가를 저장했어요. 설정 버전 {v}', 'Price saved, config version {v}'),
    complex: e('複合價目', '복합 가격', 'Compound pricing'),
    complexHint: e('影片、音樂或分時階梯價目無法用一個數表示，請到「已發布模型」編輯', '영상, 음악, 시간대/구간 가격은 숫자 하나로 나타낼 수 없어요. 「게시된 모델」에서 수정해 주세요', 'Compound pricing cannot be one number; edit under Published models'),
    unknownCost: e('{n} 次成本未知', '원가 미확인 {n}회', '{n} unknown-cost calls'),
    empty: e('這段時間沒有能力呼叫', '이 기간에 호출이 없어요', 'No capability calls in this period'),
    recentTitle: e('最近呼叫', '최근 호출', 'Recent calls'),
    recentCopy: e('按時間倒序的成功扣費流水', '시간 역순의 성공 과금 내역', 'Successful billing records, newest first'),
    recentEmpty: e('沒有流水', '내역이 없어요', 'No records'),
    rTime: e('時間', '시각', 'Time'),
    rOrg: e('帳號', '계정', 'Account'),
    rChannel: e('渠道 / 上遊模型', '채널 / 업스트림 모델', 'Channel / upstream model'),
    rDuration: e('耗時', '소요 시간', 'Duration'),
    rCost: e('花費', '비용', 'Charge'),
    costUnknown: e('未知', '미확인', 'unknown'),
  },
}
